import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["dispatch", "apiWrapper", "postponeWithAppLoader"];
import _capitalize from 'hs-lodash/capitalize';
import _get from 'hs-lodash/get';
import _partition from 'hs-lodash/partition';
import _uniq from 'hs-lodash/uniq';
import { ACTIONS, SCREENS } from '../../constants/Tracking';
import { ASSOCIATION_OBJECT_PROPERTIES } from '../../constants/associations';
import { associationTypesToCall, featureTypes, objectTypes, pluralTypeToSingle, singularTypeToPlural } from '../../constants/types';
import { showApiAlert } from '../../utils/alerts';
import captureException from '../../utils/captureException';
import { associationSectionPromise, isDataOutdated, searchObjectFilterGroups } from '../../utils/helpers';
import { DISASSOCIATE_OBJECT_FULFILLED, SET_ASSOCIATED_OBJECTS, SET_ASSOCIATIONS, TOGGLE_LOADING } from './reducer';
export const getAssociationSectionList = ({
  dispatch,
  apiWrapper,
  postponeWithAppLoader
}, {
  callObjectId,
  toObjectType,
  featuresStatus,
  isPostponedAction = false,
  setSkipRefresh,
  associations
}) => {
  if (!isPostponedAction) {
    dispatch({
      type: TOGGLE_LOADING,
      payload: true
    });
  }
  const listOfTypes = Object.keys(objectTypes);
  const promiseArray = toObjectType ? [associationSectionPromise({
    callObjectId,
    toObjectType: pluralTypeToSingle[toObjectType],
    fetchFunction: apiWrapper.fetchAssociationSectionList,
    setSkipRefresh
  })] : listOfTypes.filter(type => _get(featuresStatus, featureTypes[type], {
    available: false
  }).available).map(type => associationSectionPromise({
    callObjectId,
    toObjectType: pluralTypeToSingle[type],
    fetchFunction: apiWrapper.fetchAssociationSectionList,
    setSkipRefresh
  }));
  return Promise.allSettled(promiseArray).then(responseList => {
    const [sectionsWithObjects, sectionsWithErrors] = _partition(responseList, response => response.status === 'fulfilled');
    if (sectionsWithObjects.length) {
      sectionsWithObjects.forEach(response => {
        if (response.status === 'fulfilled') {
          const storeAssociations = associations[singularTypeToPlural[response.value.type] || response.value.type] || [];
          const actionPayload = {
            type: singularTypeToPlural[response.value.type] || response.value.type,
            list: response.value.results
          };
          if (isPostponedAction && isDataOutdated(response.value.results, storeAssociations) && postponeWithAppLoader) {
            postponeWithAppLoader(() => dispatch({
              type: SET_ASSOCIATIONS,
              payload: actionPayload
            }));
          } else {
            dispatch({
              type: SET_ASSOCIATIONS,
              payload: actionPayload
            });
          }
        }
      });
    }
    if (sectionsWithErrors.length) {
      const firstError = sectionsWithErrors[0];
      if (firstError.status === 'rejected') {
        throw firstError.reason;
      }
    }
  }).catch(captureException).finally(() => {
    if (!isPostponedAction) {
      dispatch({
        type: TOGGLE_LOADING,
        payload: false
      });
    }
  });
};
export const associateObjects = ({
  dispatch,
  apiWrapper,
  usageTracker,
  postponeWithAppLoader
}, {
  cb,
  associationCandidates,
  callObjectId,
  objectType,
  featuresStatus,
  associations
}) => {
  const associationType = associationTypesToCall[objectType];
  const list = associationCandidates.map(el => JSON.parse(el));
  return Promise.allSettled(list.map(item => apiWrapper.associateObject({
    objectType,
    associationType,
    objectId: +item.id,
    toObjectId: callObjectId
  }))).then(responseList => {
    const [sectionsWithObjects, sectionsWithErrors] = _partition(responseList, response => response.status === 'fulfilled');
    if (sectionsWithObjects.length) {
      usageTracker.trackUsage(Object.assign({
        screen: SCREENS[`associate${_capitalize(pluralTypeToSingle[objectType])}`],
        type: pluralTypeToSingle[objectType]
      }, ACTIONS.associations.associated));
      getAssociationSectionList({
        dispatch,
        apiWrapper,
        postponeWithAppLoader,
        usageTracker
      }, {
        callObjectId: +callObjectId,
        toObjectType: objectType,
        featuresStatus,
        associations
      });
    }
    if (typeof cb === 'function') cb();
    if (sectionsWithErrors.length) {
      const firstError = sectionsWithErrors[0];
      if (firstError.status === 'rejected') {
        showApiAlert(firstError.reason);
        throw firstError.reason;
      }
    }
  }).catch(captureException);
};
export const disassociateObject = ({
  dispatch,
  apiWrapper,
  usageTracker
}, data) => {
  dispatch({
    type: TOGGLE_LOADING,
    payload: true
  });
  apiWrapper.removeAssociationObject(Object.assign({}, data, {
    objectId: +data.objectId,
    toObjectId: +data.toObjectId
  })).then(() => {
    usageTracker.trackUsage(Object.assign({
      screen: SCREENS[`associate${_capitalize(pluralTypeToSingle[data.objectType])}`],
      type: pluralTypeToSingle[data.objectType]
    }, ACTIONS.associations.dissociated));
    dispatch({
      type: DISASSOCIATE_OBJECT_FULFILLED,
      payload: data
    });
  }).catch(err => {
    showApiAlert(err);
    captureException(err);
  }).finally(() => {
    dispatch({
      type: TOGGLE_LOADING,
      payload: false
    });
  });
};
export const getAssociatedObjects = (_ref, {
  query = '',
  objectType,
  isPostponedAction = false,
  cb,
  ids,
  after = '0',
  associationLoading,
  properties,
  associations,
  associatedObjects
}) => {
  let {
      dispatch,
      apiWrapper,
      postponeWithAppLoader
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const associationList = associations[objectType] || [];
  const entitites = associatedObjects[objectType];
  // we check if we already make request for this entitites
  // if we did, prevent to call api again
  const areEntititesLoaded = (ids && ids.length ? ids : associationList.map(item => item.id).filter(v => v)).every(id => entitites[id]);
  if (!areEntititesLoaded && (associationList.length || ids && ids.length)) {
    dispatch({
      type: TOGGLE_LOADING,
      payload: associationLoading
    });
    const associationCardProperties = properties[pluralTypeToSingle[objectType]].associationCardProperties;
    const defaultSidebarProperties = properties[pluralTypeToSingle[objectType]].defaultSidebarProperties;
    const searchProperties = _uniq([...associationCardProperties, ...defaultSidebarProperties, ...ASSOCIATION_OBJECT_PROPERTIES[objectType]]);
    const filterGroups = searchObjectFilterGroups(ids && ids.length ? ids.map(id => ({
      id,
      type: associationTypesToCall.contacts
    })) : associationList);
    const data = {
      objectType,
      query,
      after,
      limit: 50,
      properties: searchProperties,
      filterGroups
    };
    apiWrapper.searchObject(data).then(response => {
      const setAssociations = () => dispatch({
        type: SET_ASSOCIATED_OBJECTS,
        payload: {
          list: response.results,
          type: objectType
        }
      });
      if (isPostponedAction && postponeWithAppLoader) {
        postponeWithAppLoader(setAssociations);
      } else {
        setAssociations();
      }
      const responseAfter = _get(response, 'paging.next.after', '0');
      if (Number(responseAfter)) {
        getAssociatedObjects(Object.assign({
          dispatch,
          apiWrapper
        }, rest), {
          objectType,
          query,
          isPostponedAction,
          cb,
          ids,
          after: responseAfter,
          associationLoading,
          properties,
          associatedObjects,
          associations
        });
      }
    }).catch(error => {
      captureException(error);
    }).finally(() => {
      if (typeof cb === 'function') cb();
      dispatch({
        type: TOGGLE_LOADING,
        payload: false
      });
    });
  } else {
    if (typeof cb === 'function') cb();
  }
};